import type { ComponentType } from "react";
import { createBrowserRouter, redirect, useNavigate, useRevalidator } from "react-router";
import type { LoaderFunctionArgs, RouteObject } from "react-router";

import { Button, Container, Icon, PortalLayout, PortalPanelBody, Typography } from "../design-system";
import { STEP_TABLE } from "./route-table.ts";
import type { StepId } from "./route-table.ts";
import type { SectionStateEntry } from "./types.ts";
import { evaluateStepOrderGuard } from "./step-guard.ts";
import { pendingShellCopy, reloadFailureCopy } from "./reload-failure-copy.ts";

/**
 * Router construction (TG-4). ADR-20260826-srf0: React Router v7,
 * `createBrowserRouter`, history-API paths — no hash routing, no
 * `<ScrollRestoration>` anywhere in the tree (ADR-20260826-84dz).
 *
 * ADR-20260826-vbpt: this file contains zero step ids and zero section
 * ids as literals. The route array is built by mapping `STEP_TABLE`
 * (route-table.ts's frozen data), never by naming a step here — adding a
 * step or a section is exactly a route-table.ts change; nothing in this
 * mapping needs to change to pick it up.
 *
 * ADR-20260824-gps6 / the adapter rule: this file imports design-system
 * components only (`Alert`, `Container`, `Typography`) for the loading
 * and error shell every step route shares — never `@mui/*` directly.
 *
 * ADR-20260918-dd9e (AC-8): the step-order guard is one route `loader`,
 * attached identically to every step route by this same mapping,
 * evaluated by React Router before the route's component ever renders.
 * `evaluateStepOrderGuard` (step-guard.ts) is the pure decision; this
 * file's only job is running it with real, loader-supplied data and
 * turning a "blocked" result into a thrown `redirect()` — React Router's
 * own mechanism for "never render the requested step; render the target
 * instead." A blocked step is never returned from the loader as a value
 * (which would let the router try to render it) — it is thrown, so
 * React Router replaces the requested navigation with a new one rather
 * than surfacing anything about the requested step.
 *
 * ADR-20260918-dd9e / ADR-20260921-xt8q: this file never imports
 * section-state.ts and never calls `computeSectionStates` itself — it
 * holds no section-state knowledge. `StepGuardContext` carries
 * already-resolved `sectionStates`, computed once by whoever supplies
 * `loadGuardContext` (App.tsx's composition root), using the same shared
 * computation the route components that need per-section state also
 * call. This module only ever reads that computed result.
 */

/** The data every step route's guard loader needs. Supplied by whatever
 * composes the router with real ports (App.tsx) — this file performs no
 * I/O of its own and imports no port implementation, so it stays free of
 * any concrete read mechanism (mirrors resume.ts/section-state.ts's own
 * "no I/O" discipline). `sectionStates` is already-computed
 * (section-state.ts's `computeSectionStates`, called by the supplier of
 * `loadGuardContext`, never by this file — ADR-20260921-xt8q) — this
 * module never sees `renewalData` or `confirmedAnswers` directly and
 * never has an opportunity to recompute section state its own way. */
export interface StepGuardContext {
  readonly sectionStates: readonly SectionStateEntry[];
  /**
   * KNOWN GAP, flagged rather than worked around by widening route-table.ts
   * or resume.ts (both out of this task group's file scope, and both
   * deliberately free of any notion of a single-purpose step's own
   * completion — section-state.ts's own header comment: "steps hosting no
   * sections at all ... never need this module's attention"). Because of
   * that, resolveResumeTarget can never target a step that comes after
   * the first section-less step in declared order — it has no signal
   * that such a step was ever completed, only that no earlier section is
   * open. Left unaddressed, the LAST declared step is permanently
   * unreachable through this guard alone.
   *
   * `settledBeyondTarget: <stepId>` says "the step named here has been
   * completed by some means this pure section model cannot express" —
   * it carries the settled step's own identity (the `StepId` type, not a
   * literal named in this file — ADR-20260826-vbpt) rather than a bare
   * boolean, precisely so `isHostlessStepSettled` (the predicate
   * `createStepGuardLoader` builds from this field and forwards into
   * `evaluateStepOrderGuard`, which forwards it on into
   * `resolveResumeTarget` unchanged) only ever answers "settled" for the
   * step actually named. OBJ-36: a bare `true` carries no step identity
   * and is honoured against whatever the guard happens to resolve —
   * including the very first, earliest-unsettled step at the start of a
   * journey with nothing confirmed — which lets a caller that means
   * "declaration is done" also silently unlock one step past
   * confirm-details. The identity comparison
   * (`context.settledBeyondTarget === candidateStepId`) closes that: a
   * stale or over-broad claim can never mark a step "settled" other than
   * the one it actually names.
   *
   * OBJ-131/FIND-191: this field now feeds the guard's OWN target
   * resolution (via `resolveResumeTarget`'s `isHostlessStepSettled`
   * parameter), not a parallel admit-only special case layered on top of
   * it — there is exactly one definition of "the earliest unsettled
   * step" (ADR-20260918-dd9e, single shared computation), so a direct
   * request for a step whose hosted sections are all inapplicable (the
   * early-return branch below) redirects to that SAME recomputed target,
   * not to a step this field has already reported settled.
   *
   * The real completion signal is wired from App.tsx's own save/confirm
   * path (`settledBeyondTargetRef`, TG-8).
   */
  readonly settledBeyondTarget?: StepId;
  /**
   * ADR-20261016-q2mt (TG-6), extended by ADR-20261016-y2kd (TG-8): true
   * for a contributor session, false (or absent) for an owner.
   * `sectionStates`/`resolveResumeTarget`'s whole model is the OWNER's
   * linear progress through the step table; a contributor's own progress
   * is tracked separately (TG-2's assignment and contributor-section
   * routes, AC-19), so the order guard built against that model has
   * nothing meaningful to say about where a contributor "is".
   * `createStepGuardLoader` (router.tsx) only ever consults this field for
   * the steps `route-table.ts` names `allowsContributorStepOrderExemption`
   * ("exposures" and "received", as of ADR-20261016-y2kd) — it does NOT
   * exempt a contributor session from the order guard on any other step,
   * which still goes through the ordinary computation ("confirm-details"
   * and "declaration" stay owner-only and already render their own
   * not-permitted view for a contributor regardless of this field,
   * independently of the guard; the server's 403 outside the delegated
   * set remains the actual enforcement either way). The original
   * ADR-20261016-q2mt grant named a contributor session as always allowed
   * through to "received" specifically so a contributor's post-submit
   * navigation there (no query string: TG-6's `onContributorSubmitted`)
   * is never redirected by a resolved target computed from the owner's
   * confirmed sections, which a contributor's own submit does not settle.
   * ADR-20261016-y2kd extends the same reasoning to "exposures": a
   * contributor's delegated Start control, and its own delegated section
   * instances, open that step directly, with no owner step order to
   * satisfy. `App.tsx` computes this once via `resolveCapability`, the
   * sanctioned capability/-internal boundary for this check
   * (ADR-20260826-79rv's containment rule).
   */
  readonly isContributorSession?: boolean;
}

export interface RouterDependencies {
  readonly loadGuardContext: () => Promise<StepGuardContext>;
  /**
   * OBJ-74/FIND-77: `RouteErrorShell`'s own retry previously called only
   * `useRevalidator().revalidate()`, which re-runs the matched route's
   * loader (this step's own guard read) and nothing else. When a real
   * outage fails BOTH the guard's read and the composition root's own
   * `useCaptureState` read together (the ordinary shape of a 5xx or
   * network blip — every caller hits the same endpoints in the same
   * click), a retry that clears only the guard's failure leaves the
   * composition root's read latched `"failed"`; the step route then
   * mounts on that stale snapshot and bounces the customer to `"/"`,
   * where the landing route's own independent read succeeds and renders
   * the ordinary not-started/in-progress state with no failure shown —
   * the very shape AC-9 exists to forbid. `onRetry`, when supplied, is
   * invoked before `revalidate()` so the composition root's own read is
   * requested again in the same click, never a second guard concept
   * invented in this file.
   */
  readonly onRetry?: () => void;
}

/** One component per declared step, keyed by the same closed `StepId`
 * union route-table.ts declares — supplied by the caller (App.tsx),
 * never imported here, so this file never has to know which feature
 * module owns which step's screen. */
export type StepRouteComponents = Readonly<Record<StepId, ComponentType>>;

/**
 * design-system-only fallback shown while a step route's loader is
 * pending (React Router's `HydrateFallback`/pending-navigation UI).
 * Mandatory Design System Agent involvement for this task group: the
 * route shell below mounts `Container`/`Typography` adapters, never
 * `@mui/*` directly (ADR-20260824-gps6).
 */
function RouteLoadingShell() {
  return (
    <PortalLayout>
      <Container>
        <Typography variant="body">{pendingShellCopy.loadingLabel}</Typography>
      </Container>
    </PortalLayout>
  );
}

/** design-system-only fallback shown when a step route's loader throws
 * anything other than the guard's own redirect (e.g. the guard-context
 * read itself failing) — never a blank screen. OBJ-73: this shell must
 * carry a REAL retry control, not only the words "Try again" — `
 * useRevalidator` re-runs every currently-matched route's loader
 * (including this step's own guard loader) without a full navigation, so
 * retrying here re-attempts exactly the read that threw.
 *
 * OBJ-135/FIND-198: this is one of the four surfaces the objection named
 * — a full-page takeover with only a retry leaves the customer stranded
 * if the retry itself keeps failing. `useNavigate` to `"/"` is the same
 * root path `InlineReloadFailure`'s own `onBackToRenewal` call sites use
 * (App.tsx) — never a `STEP_TABLE` literal, so this stays within
 * ADR-20260826-vbpt's "zero step/section literals in this file" rule. */
function RouteErrorShell({ onRetry }: { readonly onRetry?: () => void }) {
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  return (
    <PortalLayout>
      <Container>
        <PortalPanelBody id="route-error" expanded tone="error" title={reloadFailureCopy.title} testId="route-error">
          <Typography variant="body">{reloadFailureCopy.message}</Typography>
          <Button
            emphasis="primary"
            onClick={() => {
              // OBJ-74/FIND-77: request the composition root's own read
              // again FIRST, so a retry after an outage that failed both
              // reads together clears the stale one this loader does not
              // own, then revalidate this route's own guard loader.
              onRetry?.();
              void revalidator.revalidate();
            }}
            testId="route-error-retry"
          >
            {reloadFailureCopy.retryLabel}
          </Button>
          <Button
            emphasis="quiet"
            onClick={() => navigate("/")}
            startIcon={<Icon name="chevronLeft" />}
            testId="route-error-back-to-renewal"
          >
            {reloadFailureCopy.backToRenewalLabel}
          </Button>
        </PortalPanelBody>
      </Container>
    </PortalLayout>
  );
}

/** design-system-only fallback for a non-step route (`/`, `/sign-in` and
 * its children, `/redeem`, `/renewal-status`, `/renewal/not-permitted`,
 * `*`) whose component throws while rendering (DEBT-20261015-qqwe). Same
 * shared layout, copy and "Back to your renewal" control as
 * `RouteErrorShell`, but no retry: `useRevalidator` re-runs loaders, and
 * these routes have none, so it would not recover a render error. */
export function PageErrorShell() {
  const navigate = useNavigate();
  return (
    <PortalLayout>
      <Container>
        <PortalPanelBody id="page-error" expanded tone="error" title={reloadFailureCopy.title} testId="page-error">
          <Typography variant="body">{reloadFailureCopy.message}</Typography>
          <Button
            emphasis="quiet"
            onClick={() => navigate("/")}
            startIcon={<Icon name="chevronLeft" />}
            testId="page-error-back-to-renewal"
          >
            {reloadFailureCopy.backToRenewalLabel}
          </Button>
        </PortalPanelBody>
      </Container>
    </PortalLayout>
  );
}

/**
 * Builds the guard loader for one step. Reads guard context fresh on
 * every navigation to that step (no caching here — a caller that wants
 * caching owns it inside its own `loadGuardContext` implementation) and
 * evaluates the guard directly against the already-computed
 * `sectionStates` it carries (ADR-20260921-xt8q — this file never calls
 * `computeSectionStates` itself), either allowing the navigation to
 * proceed (returns `null` — nothing for the route component to consume
 * from the loader) or throwing `redirect()` at the earliest unsettled
 * step's own path, read from `STEP_TABLE` rather than constructed here.
 */
function createStepGuardLoader(stepId: StepId, dependencies: RouterDependencies) {
  const requestedStepDescriptor = STEP_TABLE.find((step) => step.id === stepId);
  return async function stepGuardLoader({ request }: LoaderFunctionArgs) {
    // ADR-20261016-q2mt (TG-6), extended by ADR-20261016-y2kd (TG-8): two
    // independent guard exceptions, each scoped by its own data field on
    // `route-table.ts` (never a step-id literal here — ADR-20260826-vbpt):
    //   1. `?outcome=saved` on the request URL, scoped to whichever step
    //      `route-table.ts` names `allowsSaveOutcomeOverride` (today only
    //      "received") — App.tsx's own "Save and finish later" navigation
    //      is the only call site that ever adds this query string, always
    //      to that one step's path.
    //   2. A contributor session (`StepGuardContext.isContributorSession`
    //      doc comment), scoped to whichever step(s) `route-table.ts` names
    //      `allowsContributorStepOrderExemption` ("exposures" and
    //      "received", as of ADR-20261016-y2kd) — the order guard's model
    //      is the owner's, and does not apply to a contributor's own,
    //      separately-tracked progress; a contributor's delegated Start
    //      control and its own delegated section instances open
    //      "exposures" directly, and `onContributorSubmitted` (App.tsx)
    //      navigates to "received" with no query string at all after a
    //      real, server-confirmed submit.
    // Neither exception reaches any step outside its own named set: a
    // request for `<some other step>?outcome=saved`, or a contributor's
    // direct request for "confirm-details" or "declaration", still goes
    // through the ordinary order computation below exactly as before —
    // both of those render their own not-permitted view for a contributor
    // regardless, and the server's 403 outside the delegated set remains
    // the actual enforcement either way.
    const allowsSavedOutcome = requestedStepDescriptor?.allowsSaveOutcomeOverride === true;
    const allowsContributorExemption = requestedStepDescriptor?.allowsContributorStepOrderExemption === true;
    const requestedOutcomeSaved = new URL(request.url).searchParams.get("outcome") === "saved";
    const context = await dependencies.loadGuardContext();
    if ((allowsSavedOutcome && requestedOutcomeSaved) || (allowsContributorExemption && context.isContributorSession === true)) {
      return null;
    }
    // OBJ-131/FIND-191: the settlement claim feeds the guard's OWN target
    // resolution now, not a parallel admit-only special case layered on
    // top of it (ADR-20260918-dd9e, single shared computation) — the
    // identity check this closure performs (`context.settledBeyondTarget
    // === stepId`) is exactly the comparison OBJ-36 requires: the claim
    // only ever bypasses a step it actually names, never whichever step
    // the guard happens to resolve.
    const isHostlessStepSettled = (candidateStepId: StepId) => context.settledBeyondTarget === candidateStepId;
    const guardResult = evaluateStepOrderGuard(stepId, context.sectionStates, STEP_TABLE, isHostlessStepSettled);

    if (guardResult.allowed) {
      return null;
    }

    const redirectStep = STEP_TABLE.find((step) => step.id === guardResult.redirectTarget);
    if (redirectStep === undefined) {
      // Cannot happen given evaluateStepOrderGuard's own contract (it
      // resolves redirectTarget from this same stepTable), but this
      // module never renders the requested step regardless — surfacing
      // the error rather than silently allowing the request through.
      throw new Error(
        `router.tsx: step-order guard named redirect target "${String(guardResult.redirectTarget)}", which is not in STEP_TABLE`,
      );
    }

    throw redirect(redirectStep.path);
  };
}

/**
 * Maps `STEP_TABLE` into a `RouteObject` per step, each carrying its own
 * guard loader. This is the ONLY place the step table becomes routes —
 * adding a row to `STEP_TABLE` (route-table.ts) is the whole change
 * needed to add a route; nothing here names the new step.
 */
export function buildStepRoutes(components: StepRouteComponents, dependencies: RouterDependencies): RouteObject[] {
  return STEP_TABLE.map((step) => ({
    path: step.path,
    Component: components[step.id],
    loader: createStepGuardLoader(step.id, dependencies),
    HydrateFallback: RouteLoadingShell,
    errorElement: (
      <RouteErrorShell {...(dependencies.onRetry !== undefined ? { onRetry: dependencies.onRetry } : {})} />
    ),
  }));
}

/**
 * Constructs the portal's router. `createBrowserRouter` (history-API
 * paths, no hash routing) over exactly the routes `buildStepRoutes`
 * derives from `STEP_TABLE`. No `<ScrollRestoration>` is rendered by
 * this module or any component it owns (ADR-20260826-84dz) — there is
 * deliberately no such element anywhere in this file's tree.
 */
export function createAppRouter(components: StepRouteComponents, dependencies: RouterDependencies) {
  return createBrowserRouter(buildStepRoutes(components, dependencies));
}
